import { onlineCopyText, type LocalSyncHealthDto, type OnlineCopyTextKey } from '@his/shared';
import { dateTime } from '@/lib/utils';

const REASON_KEY: Record<string, OnlineCopyTextKey> = {
  FK_MISSING: 'onlineCopy.reason.waitingForLinked',
  RETRY_LIMIT: 'onlineCopy.reason.waitingForLinked',
  UNKNOWN_TABLE: 'onlineCopy.reason.needsNewerService',
  UNKNOWN_COLUMN: 'onlineCopy.reason.needsNewerService',
  CONSTRAINT: 'onlineCopy.reason.rule',
  DATA: 'onlineCopy.reason.value',
};

export function sizeLabel(bytes: number | null | undefined): string {
  const value = bytes ?? 0;
  if (value >= 1024 * 1024 * 1024) return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;
  return `${value} B`;
}

export function notAcceptedCounts(parkedByCode: Record<string, number> | undefined): string {
  const byReason = new Map<string, number>();
  for (const [code, count] of Object.entries(parkedByCode ?? {})) {
    if (count <= 0) continue;
    const label = onlineCopyText(REASON_KEY[code] ?? 'onlineCopy.reason.other');
    byReason.set(label, (byReason.get(label) ?? 0) + count);
  }
  return [...byReason.entries()].map(([label, count]) => `${count} ${label}`).join(', ');
}

export function onlineCopyLines(health: LocalSyncHealthDto, hasImaging: boolean): string[] {
  const lines: string[] = [onlineCopyText('onlineCopy.safeHere')];
  const live = health.backlogLive ?? 0;
  lines.push(live > 0 && health.oldestPendingLiveAt ? onlineCopyText('onlineCopy.hasEverythingUpTo', { time: dateTime(health.oldestPendingLiveAt) }) : onlineCopyText('onlineCopy.upToDate'));
  if (live > 0) lines.push(onlineCopyText('onlineCopy.liveWaiting', { n: live }));
  if ((health.backlogBulk ?? 0) > 0) lines.push(onlineCopyText('onlineCopy.bulkWaiting', { n: health.backlogBulk ?? 0 }));
  if ((health.filesWaiting ?? 0) > 0) {
    lines.push(onlineCopyText(hasImaging ? 'onlineCopy.filesWaitingImaging' : 'onlineCopy.filesWaiting', { n: health.filesWaiting ?? 0, size: sizeLabel(health.filesWaitingBytes) }));
  }
  if ((health.filesGivenUp ?? 0) > 0) lines.push(onlineCopyText('onlineCopy.filesGivenUp', { n: health.filesGivenUp ?? 0 }));
  const counts = notAcceptedCounts(health.parkedByCode);
  if (counts) lines.push(onlineCopyText('onlineCopy.notAccepted', { counts }));
  if (health.pausedReason === 'CLOUD_OLDER') lines.push(onlineCopyText('onlineCopy.pausedCloudOlder'));
  if (health.restoreDetectedAt) lines.push(onlineCopyText('onlineCopy.restored', { date: dateTime(health.restoreDetectedAt) }));
  if (health.compareSummary) {
    const result = health.compareSummary.differences === 0 ? onlineCopyText('onlineCopy.compareMatched') : onlineCopyText('onlineCopy.compareDifferences', { n: health.compareSummary.differences });
    lines.push(onlineCopyText('onlineCopy.lastCompared', { date: dateTime(health.compareSummary.at), result }));
  }
  return lines;
}

export function onlineCopyBannerLines(health: LocalSyncHealthDto, now: number = Date.now()): string[] {
  const lines: string[] = [];
  const oldest = health.oldestPendingLiveAt ? Date.parse(health.oldestPendingLiveAt) : Number.NaN;
  const hours = Number.isFinite(oldest) ? Math.floor((now - oldest) / 3_600_000) : 0;
  if ((health.backlogLive ?? 0) > 0 && hours >= 1) lines.push(onlineCopyText('onlineCopy.banner.behind', { n: hours }));
  if (health.pausedReason) lines.push(onlineCopyText('onlineCopy.banner.paused'));
  if (Object.values(health.parkedByCode ?? {}).some((count) => count > 0)) lines.push(onlineCopyText('onlineCopy.banner.notAccepted'));
  if (health.restoreDetectedAt) lines.push(onlineCopyText('onlineCopy.banner.restored'));
  return lines;
}
